(() => {
  if (window.__vccHeaderShellLoaded) return;
  window.__vccHeaderShellLoaded = true;

  const states = new WeakMap();
  const scriptLoads = new Map();

  function loadScript(src, ready) {
    if (!src) return Promise.reject(new Error('Header application asset URL is unavailable.'));
    if (ready?.()) return Promise.resolve();
    if (scriptLoads.has(src)) return scriptLoads.get(src);
    const promise = new Promise((resolve, reject) => {
      const normalized = new URL(src, document.baseURI).href;
      let existing = Array.from(document.scripts).find((script) => script.src === normalized);
      if (existing?.dataset.vccHeaderApplicationFailed === 'true') {
        existing.remove();
        existing = null;
      }
      const script = existing || document.createElement('script');
      script.src = normalized;
      script.defer = true;
      script.dataset.vccHeaderApplication = 'true';
      const fail = (message) => {
        script.dataset.vccHeaderApplicationFailed = 'true';
        if (script.dataset.vccHeaderApplication === 'true') script.remove();
        reject(new Error(message));
      };
      script.addEventListener('load', () => {
        if (ready && !ready()) fail(`Header application did not initialize: ${src}`);
        else resolve();
      }, { once: true });
      script.addEventListener('error', () => fail(`Header application failed to load: ${src}`), { once: true });
      if (!existing) document.head.appendChild(script);
      else if (ready?.()) resolve();
    }).catch((error) => {
      scriptLoads.delete(src);
      throw error;
    });
    scriptLoads.set(src, promise);
    return promise;
  }

  function safeJson(node) {
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('[VCC header] Optional search configuration is invalid.', error);
      return {};
    }
  }

  function initHeader(header) {
    if (!(header instanceof Element) || states.has(header)) return;
    const root = header.closest('.vcc-site-header');
    if (!root) return;

    const lifecycle = new AbortController();
    const state = {
      lifecycle,
      scrollFrame: 0,
      scrolled: header.classList.contains('is-scrolled'),
      cartRefreshTimer: 0,
      cartRefreshPromise: null,
      cartRefreshAfterCurrent: false,
      cartSignalVersion: 0,
      typewriterPromise: null,
      searchPromise: null,
      searchReplay: new WeakSet(),
      searchSubmitReplay: new WeakSet(),
    };
    states.set(header, state);
    header.dataset.vccHeaderShellReady = 'true';

    const listen = (target, type, listener, options = {}) => {
      if (!target) return;
      const normalized = typeof options === 'boolean' ? { capture: options } : { ...options };
      normalized.signal = lifecycle.signal;
      target.addEventListener(type, listener, normalized);
    };

    const syncScroll = () => {
      state.scrollFrame = 0;
      const next = window.scrollY > 12;
      if (next === state.scrolled) return;
      state.scrolled = next;
      header.classList.toggle('is-scrolled', next);
    };
    const queueScroll = () => {
      if (!state.scrollFrame) state.scrollFrame = window.requestAnimationFrame(syncScroll);
    };
    syncScroll();
    listen(window, 'scroll', queueScroll, { passive: true });

    root.querySelectorAll('[data-vcc-country-flag]').forEach((flag) => {
      if (!flag.querySelector('img')) flag.textContent = flag.getAttribute('data-country-code') || '';
    });
    root.querySelectorAll('[data-vcc-localization-select]').forEach((select) => {
      listen(select, 'change', () => {
        if (select.value && select.form) select.form.submit();
      });
    });
    root.querySelectorAll('[data-vcc-native-search]').forEach((form) => {
      const input = form.querySelector('input[type="search"][name="q"]');
      listen(input, 'keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        event.preventDefault();
        HTMLFormElement.prototype.submit.call(form);
      });
    });

    const cartBadges = Array.from(root.querySelectorAll('[data-vcc-cart-count]'));
    const cartControls = Array.from(root.querySelectorAll('[data-vcc-cart-control]'));
    const setCartCount = (value) => {
      if (lifecycle.signal.aborted) return;
      state.cartRefreshAfterCurrent = false;
      state.cartSignalVersion += 1;
      const count = Number(value) || 0;
      cartBadges.forEach((badge) => { badge.textContent = String(count); });
      cartControls.forEach((control) => {
        control.setAttribute('aria-label', `Cart, ${count}${count === 1 ? ' item' : ' items'}`);
      });
    };
    const clearScheduledCartRefresh = () => {
      if (!state.cartRefreshTimer) return;
      window.clearTimeout(state.cartRefreshTimer);
      state.cartRefreshTimer = 0;
    };
    const refreshCartCount = () => {
      clearScheduledCartRefresh();
      if (lifecycle.signal.aborted) return Promise.resolve();
      if (!cartBadges.length) return Promise.resolve();
      if (state.cartRefreshPromise) {
        state.cartRefreshAfterCurrent = true;
        return state.cartRefreshPromise;
      }
      const signalVersion = state.cartSignalVersion;
      state.cartRefreshPromise = fetch('/cart.js', { credentials: 'same-origin' })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Cart read failed.')))
        .then((cart) => {
          if (signalVersion === state.cartSignalVersion) setCartCount(cart.item_count);
        })
        .catch(() => {})
        .then(() => {
          state.cartRefreshPromise = null;
          if (!state.cartRefreshAfterCurrent || lifecycle.signal.aborted) return;
          state.cartRefreshAfterCurrent = false;
          scheduleCartRefresh(0);
        });
      return state.cartRefreshPromise;
    };
    const scheduleCartRefresh = (delay = 180) => {
      if (!cartBadges.length || lifecycle.signal.aborted) return;
      clearScheduledCartRefresh();
      state.cartRefreshTimer = window.setTimeout(() => {
        state.cartRefreshTimer = 0;
        refreshCartCount();
      }, delay);
    };
    const cartCountFromPayload = (payload) => {
      const detail = payload?.detail || {};
      const cart = payload?.cart || detail.cart || {};
      const candidates = [
        cart.totalQuantity,
        cart.item_count,
        cart.itemCount,
        detail.itemCount,
        detail.item_count,
      ];
      for (const candidate of candidates) {
        if (candidate === null || candidate === undefined || (typeof candidate === 'string' && candidate.trim() === '')) continue;
        const count = Number(candidate);
        if (Number.isFinite(count) && count >= 0) return count;
      }
      return null;
    };
    const syncCartCountFromEvent = (event) => {
      const count = cartCountFromPayload({ detail: event?.detail });
      if (count !== null) {
        clearScheduledCartRefresh();
        setCartCount(count);
        return;
      }
      if (event?.promise && typeof event.promise.then === 'function') {
        clearScheduledCartRefresh();
        state.cartRefreshAfterCurrent = false;
        state.cartSignalVersion += 1;
        const eventVersion = state.cartSignalVersion;
        Promise.resolve(event.promise)
          .then((payload) => {
            if (eventVersion !== state.cartSignalVersion) return;
            const promisedCount = cartCountFromPayload(payload);
            if (promisedCount !== null) setCartCount(promisedCount);
            else scheduleCartRefresh();
          })
          .catch(() => {
            if (eventVersion === state.cartSignalVersion) scheduleCartRefresh();
          });
        return;
      }
      state.cartSignalVersion += 1;
      scheduleCartRefresh();
    };
    listen(document, 'submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
      state.cartSignalVersion += 1;
      scheduleCartRefresh(1500);
    }, true);
    [
      'shopify:cart:lines-update',
      'vcc:cart:updated',
      'cart:update',
      'cart:updated',
      'cart:change',
      'cart:refresh',
      'ajaxProduct:added',
      'product:added',
    ].forEach((name) => {
      listen(document, name, syncCartCountFromEvent);
    });

    const startTypewriter = () => {
      if (state.typewriterPromise) return state.typewriterPromise;
      const source = root.dataset.vccHeaderTypewriterSrc;
      if (!source || !root.querySelector('[data-vcc-header-typewriter]')) return Promise.resolve();
      state.typewriterPromise = loadScript(source, () => Boolean(window.VCCHeaderTypewriter))
        .then(() => window.VCCHeaderTypewriter?.init?.(root, lifecycle.signal))
        .catch((error) => {
          state.typewriterPromise = null;
          throw error;
        });
      return state.typewriterPromise;
    };
    listen(document, 'vcc:first-hero-painted', () => startTypewriter().catch(() => {}), { once: true });
    listen(root, 'focusin', (event) => {
      if (event.target.closest('[data-vcc-header-typewriter]')) startTypewriter().catch(() => {});
    }, true);
    if (window.__vccFirstHeroPainted || document.documentElement.dataset.vccFirstHeroPainted === 'true') startTypewriter().catch(() => {});

    const configNode = root.querySelector('[data-vcc-header-search-config]');
    const searchConfig = safeJson(configNode);
    const searchSelector = '[data-vcc-smart-search-open], [data-vcc-smart-search], [data-vcc-smart-search-overlay]';
    const searchTarget = (target) => target instanceof Element ? target.closest(searchSelector) : null;
    const searchControl = (target) => target instanceof Element
      ? target.closest('button, a, input, [role="button"], [tabindex]') || target
      : null;
    const ensureSearch = () => {
      if (state.searchPromise) return state.searchPromise;
      if (!searchConfig.loader) return Promise.reject(new Error('Legacy search loader is unavailable.'));
      state.searchPromise = loadScript(searchConfig.loader, () => Boolean(window.VCCHeaderSearchBootstrap))
        .then(() => {
          if (!window.VCCHeaderSearchBootstrap?.install) throw new Error('Legacy search bootstrap did not initialize.');
          return window.VCCHeaderSearchBootstrap.install(root, searchConfig, { setCartCount, refreshCartCount });
        })
        .catch((error) => {
          state.searchPromise = null;
          throw error;
        });
      return state.searchPromise;
    };
    const submitSearchFallback = (form) => {
      if (!(form instanceof HTMLFormElement)) return;
      const visible = form.querySelector('input[type="search"]');
      let query = form.querySelector('[data-vcc-smart-search-query], input[name="q"]');
      if (!query) {
        query = document.createElement('input');
        query.type = 'hidden';
        query.name = 'q';
        form.appendChild(query);
      }
      if (query !== visible) query.value = visible?.value || '';
      HTMLFormElement.prototype.submit.call(form);
    };

    const warmSearch = (event) => {
      const target = searchTarget(event.target);
      if (!target || !configNode) return;
      ensureSearch().then((runtime) => {
        if (event.type === 'focusin' && event.target.matches('input[type="search"]') && document.activeElement === event.target) {
          runtime.open(event.target.value || '');
        }
      }).catch(() => {});
    };
    ['pointerover', 'pointerdown', 'touchstart'].forEach((name) => listen(root, name, warmSearch, { capture: true, passive: true }));
    listen(root, 'focusin', warmSearch, { capture: true });

    listen(root, 'click', (event) => {
      const target = searchTarget(event.target);
      if (!target || !configNode || window.VCCHeaderSearchBootstrap?.runtimeFor?.(root)) return;
      const control = searchControl(event.target);
      if (control && state.searchReplay.has(control)) {
        state.searchReplay.delete(control);
        return;
      }
      if (control?.form instanceof HTMLFormElement && control.matches('button:not([type]), button[type="submit"], input[type="submit"], input[type="image"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      ensureSearch().then(() => {
        if (!(control instanceof Element) || !control.isConnected) return;
        state.searchReplay.add(control);
        if (typeof control.click === 'function') control.click();
        else control.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      }).catch(() => {
        if (control?.matches?.('a[href]')) window.location.assign(control.href);
        else if (control?.form instanceof HTMLFormElement) submitSearchFallback(control.form);
      });
    }, true);

    listen(root, 'submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-vcc-smart-search], [data-vcc-smart-search-overlay]') || !configNode) return;
      if (window.VCCHeaderSearchBootstrap?.runtimeFor?.(root) || state.searchSubmitReplay.has(form)) {
        state.searchSubmitReplay.delete(form);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      ensureSearch().then(() => {
        state.searchSubmitReplay.add(form);
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }).catch(() => submitSearchFallback(form));
    }, true);

    state.destroy = () => {
      lifecycle.abort();
      clearScheduledCartRefresh();
      if (state.scrollFrame) window.cancelAnimationFrame(state.scrollFrame);
      window.VCCHeaderTypewriter?.destroy?.(root);
      window.VCCHeaderSearchBootstrap?.destroy?.(root);
      header.removeAttribute('data-vcc-header-shell-ready');
      states.delete(header);
    };
  }

  function initWithin(scope = document) {
    if (scope instanceof Element && scope.matches('[data-vcc-header]')) initHeader(scope);
    scope.querySelectorAll?.('[data-vcc-header]').forEach(initHeader);
  }

  initWithin(document);
  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-vcc-header]').forEach((header) => states.get(header)?.destroy?.());
  });
})();
